import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useAuth } from '../context/AuthContext';
import NotificationBadge from './NotificationBadge';

const PAGE_TITLES = {
  '/dashboard': 'Executive Dashboard', '/projects': 'Projects', '/clients': 'Clients', '/tasks': 'Tareas y seguimiento',
  '/followups': 'Tareas y seguimiento', '/calendar': 'Calendar', '/iso-9001': 'ISO 9001:2015',
  '/security-center': 'Security Center', '/documents': 'Documents', '/email-history': 'Email Center',
  '/ndas': 'NDA y contratos',
  '/sales-docs': 'Sales Documents', '/users': 'Users', '/chat': 'Chat', '/reports': 'Reports',
  '/notifications': 'Notifications', '/activity-log': 'Activity Log', '/finance': 'Finance',
  '/fiscal': 'Fiscal Control', '/roles-permissions': 'Roles & Permissions', '/partner-quotes': 'Partner Quotes',
  '/workflow': 'Tareas y seguimiento', '/profile': 'My Profile', '/settings': 'Settings',
  '/expense-tickets': 'Expense Tickets', '/commissions': 'Commissions'
};

const ROLE_LABELS = { admin: 'Administrator', manager: 'Manager', user: 'User', client: 'Client', viewer: 'Viewer' };

const SEARCH_DESTINATIONS = [
  { terms: ['cliente', 'clientes', 'rfc'], path: '/clients' },
  { terms: ['factura', 'cfdi', 'fiscal', 'sat'], path: '/fiscal' },
  { terms: ['proyecto', 'proyectos'], path: '/projects' },
  { terms: ['tarea', 'tareas', 'actividad', 'actividades', 'seguimiento', 'pendientes', 'workflow', 'pipeline'], path: '/tasks' },
  { terms: ['finanza', 'finanzas', 'pago', 'gasto'], path: '/finance' },
  { terms: ['iso', 'calidad', 'qms'], path: '/iso-9001' },
  { terms: ['documento', 'documentos'], path: '/documents' },
  { terms: ['nda', 'confidencialidad', 'firma'], path: '/ndas' },
  { terms: ['reporte', 'reportes'], path: '/reports' }
];

export default function ExecutiveAppBar({ handleDrawerToggle, darkMode, setDarkMode, drawerWidth = 204, isSidebarCollapsed = false, onToggleSidebarCollapse }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(() => Number(localStorage.getItem('dashboardFiscalYear')) || currentYear);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const title = useMemo(() => {
    if (PAGE_TITLES[location.pathname]) return PAGE_TITLES[location.pathname];
    const match = Object.keys(PAGE_TITLES).find((path) => location.pathname.startsWith(`${path}/`));
    return match ? PAGE_TITLES[match] : 'SMTLINKS Management';
  }, [location.pathname]);

  const role = ROLE_LABELS[String(user?.role || '').toLowerCase()] || user?.role || 'User';
  const initials = String(user?.username || user?.email || 'SM').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

  const changeYear = (event) => {
    const nextYear = Number(event.target.value);
    setYear(nextYear);
    localStorage.setItem('dashboardFiscalYear', String(nextYear));
    window.dispatchEvent(new CustomEvent('smtlink:fiscal-year-change', { detail: nextYear }));
  };

  const runModuleSearch = (event) => {
    if (event.key !== 'Enter') return;
    const query = search.trim().toLowerCase();
    if (!query) return;
    const destination = SEARCH_DESTINATIONS.find((entry) => entry.terms.some((term) => query.includes(term)));
    navigate(destination?.path || '/dashboard');
    setSearch('');
    setSearchOpen(false);
  };

  const closeMenu = useCallback(() => setAnchorEl(null), []);

  return (
    <AppBar position="fixed" elevation={0} sx={{ width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` }, pt: 'env(safe-area-inset-top, 0px)', bgcolor: theme.palette.mode === 'dark' ? '#0d1a30' : '#fff', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider', transition: 'all 170ms ease' }}>
      <Toolbar sx={{ minHeight: '56px !important', px: { xs: 1, sm: 1.6 }, gap: .8 }}>
        <Tooltip title={compact ? 'Open menu' : (isSidebarCollapsed ? 'Expand menu' : 'Collapse menu')} arrow>
          <IconButton size="small" onClick={compact ? handleDrawerToggle : onToggleSidebarCollapse} aria-label="Toggle navigation">
            <MenuRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Typography noWrap sx={{ ml: .5, minWidth: 0, flex: '0 1 auto', maxWidth: { xs: 132, sm: 260, lg: 380 }, fontSize: { xs: 13, sm: 14 }, fontWeight: 800, color: theme.palette.mode === 'dark' ? 'text.primary' : '#102447' }}>{title}</Typography>
        <Box sx={{ flex: 1, minWidth: 0 }} />

        {!mobile && (
          <Select value={year} onChange={changeYear} size="small" aria-label="Ejercicio" startAdornment={<CalendarMonthOutlinedIcon sx={{ ml: .2, mr: .7, fontSize: 16, color: 'text.secondary' }} />} sx={{ height: 34, minWidth: 136, fontSize: 11, fontWeight: 700, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' } }}>
            {[currentYear, currentYear - 1, currentYear - 2].map((item) => <MenuItem key={item} value={item}>Ejercicio {item}</MenuItem>)}
          </Select>
        )}

        {!compact && (
          <TextField value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={runModuleSearch} size="small" placeholder="Search modules..." inputProps={{ 'aria-label': 'Search modules' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17 }} /></InputAdornment> }} sx={{ width: 180, '& .MuiInputBase-root': { height: 34, fontSize: 11 } }} />
        )}

        {compact && (
          <Tooltip title="Search" arrow>
            <IconButton size="small" onClick={() => setSearchOpen(true)} aria-label="Open global search">
              <SearchRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <NotificationBadge />
        <Tooltip title="Ayuda"><IconButton size="small" aria-label="Open help" onClick={() => navigate('/documents')} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}><HelpOutlineRoundedIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title={darkMode ? 'Modo claro' : 'Modo oscuro'}><IconButton size="small" aria-label={darkMode ? 'Enable light mode' : 'Enable dark mode'} onClick={() => setDarkMode((value) => !value)}>{darkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}</IconButton></Tooltip>

        <ButtonBase
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Open user menu"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
          sx={{ ml: .25, display: 'flex', alignItems: 'center', gap: .7, minWidth: 40, minHeight: 40, borderRadius: 999 }}
        >
          <Avatar sx={{ width: 30, height: 30, bgcolor: '#0d5db8', fontSize: 10.5, fontWeight: 800 }}>{initials}</Avatar>
          {!compact && <Box sx={{ maxWidth: 120 }}><Typography noWrap sx={{ fontSize: 10.5, fontWeight: 750, lineHeight: 1.15 }}>{user?.username || 'User'}</Typography><Typography noWrap sx={{ fontSize: 8.5, color: 'text.secondary', mt: .25 }}>{role}</Typography></Box>}
        </ButtonBase>
      </Toolbar>

      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} fullWidth maxWidth="xs" aria-labelledby="global-search-title">
        <DialogTitle id="global-search-title">Global search</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={runModuleSearch}
            label="Search modules"
            helperText="Type clients, projects, tasks, finance, CFDI, ISO or reports, then press Enter."
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
          />
        </DialogContent>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} TransitionComponent={Fade} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }} transformOrigin={{ horizontal: 'right', vertical: 'top' }} PaperProps={{ sx: { mt: .8, minWidth: 220, borderRadius: 2 } }}>
        <Box sx={{ px: 2, py: 1.2 }}><Typography sx={{ fontSize: 12, fontWeight: 800 }}>{user?.username || 'User'}</Typography><Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{user?.email || role}</Typography></Box>
        <Divider />
        <MenuItem onClick={() => { closeMenu(); navigate('/profile'); }}><ListItemIcon><PersonOutlineRoundedIcon fontSize="small" /></ListItemIcon>Mi perfil</MenuItem>
        <MenuItem onClick={() => { closeMenu(); navigate('/settings'); }}><ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>Settings</MenuItem>
        <Divider />
        <MenuItem onClick={() => { closeMenu(); logout(); }} sx={{ color: 'error.main' }}><ListItemIcon><LogoutRoundedIcon fontSize="small" color="error" /></ListItemIcon>Sign Out</MenuItem>
      </Menu>
    </AppBar>
  );
}
